import axios from "axios";

/**
 * Ad-hoc charges API client (js_crm_backend docs/roadmap/AD_HOC_CHARGES_SPEC.md).
 * A charge is a non-catalog line (trip charge, site not ready, access fee…)
 * added to a job after the quote. One call writes the client invoice line
 * (price = what the client pays, cost = what the provider bills us) and, by
 * default, a cost-only twin on the newest live PO for the job. The two lines
 * share entityId = charge id. Staff only; unpaid invoices only.
 */

export const CHARGE_TYPE = "charge";

export type ChargeKind = "trip" | "site_not_ready" | "fuel" | "access" | "other";

export const CHARGE_PRESETS: Array<{ key: ChargeKind; name: string; description: string }> = [
  { key: "trip", name: "Trip Charge", description: "Additional trip to the site" },
  { key: "site_not_ready", name: "Site Not Ready", description: "Crew arrived; site was not ready for installation" },
  { key: "fuel", name: "Fuel Charge", description: "Fuel for additional travel" },
  { key: "access", name: "Access Fee", description: "Special access or equipment required on site" },
  { key: "other", name: "Additional Charge", description: "" },
];

export interface ChargeInput {
  kind: ChargeKind;
  name?: string;
  description?: string;
  quantity?: number;
  unit?: string;
  /** What the provider bills us, per unit. */
  cost: number;
  /** What the client pays, per unit. Defaults to cost. */
  price?: number;
  /** Default true: also write the cost line on the newest live PO (or purchaseOrderId). */
  applyToPurchaseOrder?: boolean;
  purchaseOrderId?: string;
  reason?: string;
}

export interface AddChargeResult {
  charge: {
    id: string;
    kind: ChargeKind;
    name: string;
    description: string;
    quantity: number;
    unit: string;
    cost: number;
    price: number;
    invoiceId: string;
    purchaseOrderId: string | null;
    purchaseOrderSequence: number | null;
  };
  /** Set when the PO was skipped because the provider has already billed it. */
  purchaseOrderSkipped: string | null;
  invoice: any;
}

type Err = { error: string };
const fail = (e: any, what: string): Err => {
  const d = e?.response?.data;
  const m = d?.message || d?.error;
  return { error: Array.isArray(m) ? m.join(", ") : m ? String(m) : `${what} failed.` };
};

export const addCharge = async (invoiceId: string, input: ChargeInput): Promise<AddChargeResult | Err> => {
  try {
    const res = await axios.post(`/invoices/${invoiceId}/charges`, input);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Adding the charge");
  }
};

export const removeCharge = async (invoiceId: string, chargeId: string, reason: string): Promise<{ removed: boolean; keptOnBilledPurchaseOrders: string[]; invoice: any } | Err> => {
  try {
    const res = await axios.delete(`/invoices/${invoiceId}/charges/${chargeId}`, { data: { reason } });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Removing the charge");
  }
};
